import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Page, PageHeader, Segmented } from "../../../components/ui";
import { can, isSuperuser } from "../../../utils/roles";
import { getLogStats, getPaymentStats } from "../../../services/monitoring";
import FailedPayments from "./FailedPayments";
import MobileMoney from "./MobileMoney";
import Journal from "./Journal";
import AdminActivity from "./AdminActivity";
import Alerts from "./Alerts";
import "./logs.css";

// `logs`: tabs of the "logs" section; `adminActivity`: system admins and
// admins who manage administrators (checked again by the API).
const TABS = [
  {
    value: "payments",
    label: "Failed payments",
    icon: "ri-money-dollar-circle-line",
    logs: true,
  },
  {
    value: "mobile",
    label: "Mobile Money",
    icon: "ri-smartphone-line",
    logs: true,
  },
  { value: "journal", label: "Log", icon: "ri-file-list-2-line", logs: true },
  { value: "alerts", label: "Alerts", icon: "ri-alarm-warning-line", logs: true },
  {
    value: "admins",
    label: "Admin activity",
    icon: "ri-shield-user-line",
    adminActivity: true,
  },
];

export const canSeeAdminActivity = () => isSuperuser() || can("admins", "edit");

const Stat = ({ icon, value, label, tone, onClick }) => (
  <button
    type="button"
    className="ui-card logs-stat"
    data-tone={tone}
    onClick={onClick}
  >
    <span className="logs-stat-icon" aria-hidden="true">
      <i className={icon}></i>
    </span>
    <span>
      <strong>{value ?? "—"}</strong>
      <span>{label}</span>
    </span>
  </button>
);

/**
 * Audit and logs: failed payments and their follow-up, Mobile Money providers,
 * the whole application log (payments received included) and the activity of
 * the administrators.
 */
const Logs = () => {
  const { t } = useTranslation();
  const showLogs = can("logs");
  const showAdmins = canSeeAdminActivity();
  const tabs = TABS.filter(
    (item) => (item.logs && showLogs) || (item.adminActivity && showAdmins),
  );
  const [tab, setTab] = useState(tabs[0]?.value);
  // Filter of the log when opened from a figure ("payments received", "errors")
  const [journalLevel, setJournalLevel] = useState("");
  const [stats, setStats] = useState(null);
  const [logStats, setLogStats] = useState(null);

  const refresh = useCallback(() => {
    if (!showLogs) return;
    getPaymentStats().then(setStats);
    getLogStats().then(setLogStats);
  }, [showLogs]);
  const openJournal = (level) => {
    setJournalLevel(level);
    setTab("journal");
  };
  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <Page>
      <PageHeader
        title={t("Audit and logs")}
        subtitle={t(
          "Payments received and failed, Mobile Money providers, the whole application log and the activity of the administrators. Sensitive data is always masked.",
        )}
      />
      {showLogs && (
        <div className="logs-stats">
          <Stat
            icon="ri-wallet-3-line"
            tone="green"
            value={logStats?.last24h?.received}
            label={t("Payments received (24 h)")}
            onClick={() => openJournal("received")}
          />
          <Stat
            icon="ri-error-warning-line"
            tone="amber"
            value={stats?.toReview}
            label={t("Mobile Money payments to handle")}
            onClick={() => setTab("payments")}
          />
          <Stat
            icon="ri-bank-card-line"
            tone="red"
            value={stats?.pastDue}
            label={t("Unpaid card subscriptions")}
            onClick={() => setTab("payments")}
          />
          <Stat
            icon="ri-smartphone-line"
            tone="blue"
            value={stats ? `${stats.mobile.failureRate} %` : null}
            label={t("Mobile Money failure rate (30 days)")}
            onClick={() => setTab("mobile")}
          />
          <Stat
            icon="ri-bug-line"
            tone="violet"
            value={logStats?.last24h?.error}
            label={t("Server errors (24 h)")}
            onClick={() => openJournal("error")}
          />
        </div>
      )}
      <div className="logs-tabs">
        <Segmented
          label={t("Audit and logs")}
          value={tab}
          onChange={(value) => {
            setJournalLevel("");
            setTab(value);
          }}
          options={tabs.map((item) => ({
            value: item.value,
            label: (
              <>
                <i className={item.icon} aria-hidden="true"></i> {t(item.label)}
              </>
            ),
          }))}
        />
      </div>
      {tab === "payments" && (
        <FailedPayments stats={stats} onChange={refresh} />
      )}
      {tab === "mobile" && <MobileMoney />}
      {tab === "journal" && (
        <Journal
          key={journalLevel}
          initialLevel={journalLevel}
          showAdminActivity={showAdmins}
        />
      )}
      {tab === "admins" && <AdminActivity />}
      {tab === "alerts" && <Alerts />}
    </Page>
  );
};

export default Logs;
